import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ToggleButton } from "components/toggle";
import { useAtomValue } from "jotai";
import React from "react";
import { useArticleShareUrl } from "../hooks/useArticleShareUrl";
import { isPublished } from "../store";
import {
  useCreateShareableLink,
  useGetShareableLink,
  useToggleShareableLink,
} from "controller/hooks/kb/useKb";
import { KbShareableLink } from "shared";
import toast from "react-hot-toast";
import { useCheckUserAuth } from "controller";
import { CopyIcon } from "components";

export const KbShareLink = ({
  articleId,
  userAuthData,
}: {
  articleId: string;
  userAuthData: Record<string, any>;
}) => {
  const hasPublishedVersions = useAtomValue(isPublished);
  const { data: shareableLink, isLoading } = useGetShareableLink(
    articleId,
    hasPublishedVersions,
  );
  if (isLoading) {
    return null;
  }
  return (
    <>
      {shareableLink ? (
        <LinkItem
          shareableLink={shareableLink}
          articleId={articleId}
          userAuthData={userAuthData}
        />
      ) : (
        <CreateButton articleId={articleId} />
      )}
    </>
  );
};

export const CreateButton = ({ articleId }: { articleId: string }) => {
  const theme = useTheme();
  const { mutateAsync: createShareableLink } = useCreateShareableLink();

  const handleShareArticle = async () => {
    try {
      await createShareableLink(articleId);
      toast.success("Shareable link created successfully");
    } catch (error) {
      toast.error("Failed to create shareable link");
    }
  };
  return (
    <Stack
      sx={{
        width: "100%",
        height: "64px",
        justifyContent: "center",
        alignItems: "center",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        borderRadius: "4px",
        backgroundColor: theme.palette.neutral["005"],
        cursor: "pointer",
      }}
      onClick={handleShareArticle}
    >
      <Typography variant="button-small" color={theme.palette.primary.main}>
        Create public link
      </Typography>
    </Stack>
  );
};

export const LinkItem = ({
  shareableLink,
  articleId,
  userAuthData,
}: {
  shareableLink: KbShareableLink;
  articleId: string;
  userAuthData: Record<string, any>;
}) => {
  const theme = useTheme();
  const { mutateAsync: toggleShareableLink } = useToggleShareableLink();

  const handleToggle = async (value: boolean) => {
    try {
      await toggleShareableLink({
        articleId,
        isActive: value,
      });
      toast.success(`Link ${value ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      toast.error(`Failed to ${value ? "activate" : "deactivate"} link`);
    }
  };

  const url = useArticleShareUrl(userAuthData, shareableLink.mapping_id);
  return (
    <Stack
      direction={"row"}
      sx={{
        p: "16px",
        // width: "100%",
        minHeight: "64px",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        borderRadius: "4px",
        backgroundColor: theme.palette.neutral["005"],
        gap: "12px",
        overflow: "hidden",
        gridTemplateColumns: "40px 1fr 24px",
      }}
    >
      <Box sx={{ width: "40px" }}>
        <ToggleButton
          isChecked={shareableLink.is_active}
          onChange={handleToggle}
        />
      </Box>

      <Typography
        sx={{
          wordBreak: "break-all",
          cursor: "pointer",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        variant="link-xs"
        color={theme.palette.primary.main}
        onClick={() => {
          window.open(url, "_blank", "noopener,noreferrer");
        }}
      >
        {url}
      </Typography>
      <Box
        sx={{ width: "24px", height: "24px", flexShrink: 0, cursor: "pointer" }}
        onClick={() => {
          navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard");
        }}
      >
        <CopyIcon sx={{ fontSize: "16px" }} />
      </Box>
    </Stack>
  );
};
